import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  OnInit,
  output,
  OutputEmitterRef,
  Signal
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { BadgeModeEnum, GameCardV2Component, RedirectTypeEnum } from '@ui-components';
import { switchMap } from 'rxjs';
import { GameDetailsModel, LocalRefreshService } from '@core';
import { IonicModule } from '@ionic/angular';
import { OpenSingleChatCommandHandler } from '@messages';
import { ChatService } from '@messages/infra';
import { GetMyGamesQueryHandler, LoadMyGamesCommandHandler } from '@my-games';

@Component({
  selector: 'lib-dashboard-games-list',
  templateUrl: './dashboard-games-list.component.html',
  styleUrls: ['./dashboard-games-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [IonicModule, GameCardV2Component],
  providers: [GetMyGamesQueryHandler, OpenSingleChatCommandHandler, ChatService, LocalRefreshService]
})
export class DashboardGamesListComponent implements OnInit {
  private readonly getMyGamesQueryHandler: GetMyGamesQueryHandler = inject(GetMyGamesQueryHandler);
  private readonly loadMyGamesCommandHandler: LoadMyGamesCommandHandler = inject(LoadMyGamesCommandHandler);
  private readonly openSingleChatCommandHandler: OpenSingleChatCommandHandler = inject(OpenSingleChatCommandHandler);
  private readonly localRefreshService: LocalRefreshService = inject(LocalRefreshService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  readonly isGameAvailable: OutputEmitterRef<boolean> = output<boolean>();
  readonly gamesCount: OutputEmitterRef<number> = output<number>();
  readonly loadingChange: OutputEmitterRef<boolean> = output<boolean>();

  readonly gameRedirectType: typeof RedirectTypeEnum = RedirectTypeEnum;
  readonly badgeMode: typeof BadgeModeEnum = BadgeModeEnum;
  readonly skeletonItems: readonly number[] = [0, 1, 2];

  private readonly isLoaded: Signal<boolean> = toSignal(this.getMyGamesQueryHandler.isLoaded$(), {
    initialValue: false
  });
  readonly isLoading: Signal<boolean> = computed<boolean>(() => !this.isLoaded());

  readonly hostedGames: Signal<GameDetailsModel[]> = toSignal(this.getMyGamesQueryHandler.getFutureHosted$(), {
    initialValue: [] as GameDetailsModel[]
  });

  readonly playerApprovedGames: Signal<GameDetailsModel[]> = toSignal(
    this.getMyGamesQueryHandler.getFuturePlayerApproved$(),
    { initialValue: [] as GameDetailsModel[] }
  );

  readonly playerWaitingGames: Signal<GameDetailsModel[]> = toSignal(
    this.getMyGamesQueryHandler.getFuturePlayerWaiting$(),
    { initialValue: [] as GameDetailsModel[] }
  );

  readonly hasAnyGames: Signal<boolean> = computed<boolean>(() => {
    return !!this.hostedGames().length || !!this.playerApprovedGames().length || !!this.playerWaitingGames().length;
  });

  constructor() {
    this.setupGameAvailabilityEffect();
    this.setupLoadingEffect();
  }

  ngOnInit(): void {
    this.setupRefreshListener();
  }

  sendMessage(userId: string, displayName?: string): void {
    this.openSingleChatCommandHandler.open(userId, displayName).subscribe();
  }

  refresh(): void {
    this.localRefreshService.emit();
  }

  private setupGameAvailabilityEffect(): void {
    effect(() => {
      const total: number =
        this.hostedGames().length + this.playerApprovedGames().length + this.playerWaitingGames().length;
      this.isGameAvailable.emit(total > 0);
      this.gamesCount.emit(total);
    });
  }

  private setupLoadingEffect(): void {
    effect(() => {
      this.loadingChange.emit(this.isLoading());
    });
  }

  private setupRefreshListener(): void {
    this.localRefreshService.refresh$
      .pipe(
        switchMap(() => this.loadMyGamesCommandHandler.load()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }
}
